/**
 * WorkspaceDeploymentPage - Configuration du mode de déploiement (Preview/Production).
 * 
 * Features:
 * - Toggle preview/production
 * - Affichage statut actuel avec badge
 * - Explications des différences entre les modes
 * - Vérification permissions workspace
 * 
 * @security Mode preview bloque widget embed externe
 */
import React, { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api'
import { AlertTriangle, CheckCircle2, Rocket } from 'lucide-react'

type DeploymentMode = 'preview' | 'production'

interface Workspace {
  id: number
  name: string
  deployment_mode: DeploymentMode
}

export default function WorkspaceDeploymentPage() {
  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [mode, setMode] = useState<DeploymentMode>('preview')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // TODO: Remplacer par useWorkspace() quand disponible
  const workspaceId = 1 // Temporaire - à récupérer depuis le contexte

  useEffect(() => {
    fetchWorkspace()
  }, [])

  const fetchWorkspace = async () => {
    setLoading(true)
    try {
      const { data } = await api.get(`/workspaces/${workspaceId}`)
      setWorkspace(data)
      setMode(data.deployment_mode as DeploymentMode)
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleMode = async () => {
    if (!workspace) return
    
    const newMode: DeploymentMode = mode === 'preview' ? 'production' : 'preview'
    
    setSaving(true)
    setError(null)
    
    try {
      await api.patch(`/workspaces/${workspace.id}`, {
        deployment_mode: newMode
      })
      setMode(newMode)
      fetchWorkspace()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la mise à jour')
      // Revert
      setMode(mode)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-zinc-500">Chargement...</div>
      </div>
    )
  }

  const isProduction = mode === 'production'

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
          Mode de Déploiement
        </h1>
        <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
          Contrôlez l'accès au widget embed externe de votre workspace.
        </p>
      </div>

      {/* Statut actuel */}
      <div className={`rounded-lg border p-6 ${
        isProduction 
          ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20' 
          : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20'
      }`}>
        <div className="flex items-center gap-3">
          {isProduction ? (
            <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
          ) : (
            <AlertTriangle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          )}
          <div>
            <h2 className={`text-lg font-semibold ${
              isProduction ? 'text-green-900 dark:text-green-100' : 'text-amber-900 dark:text-amber-100'
            }`}>
              Mode {isProduction ? 'Production' : 'Preview'}
            </h2>
            <p className={`text-sm ${
              isProduction ? 'text-green-700 dark:text-green-300' : 'text-amber-700 dark:text-amber-300'
            }`}>
              {isProduction 
                ? 'Le widget embed est accessible publiquement'
                : 'Le widget embed externe est bloqué (développement uniquement)'}
            </p>
          </div>
        </div>
      </div>

      {/* Toggle */}
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <label htmlFor="production-mode" className="text-base font-medium text-zinc-900 dark:text-zinc-100">
              Activer le mode Production
            </label>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              Autorise l'utilisation du widget embed sur des sites externes
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              id="production-mode"
              type="checkbox"
              checked={isProduction}
              onChange={handleToggleMode}
              disabled={saving}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 dark:peer-focus:ring-emerald-800 rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-emerald-600"></div>
          </label>
        </div>
      </div>

      {/* Explications */}
      <div className="space-y-4">
        <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
          <h3 className="flex items-center gap-2 font-medium text-zinc-900 dark:text-zinc-100">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            Mode Preview
          </h3>
          <ul className="mt-2 space-y-1 text-sm text-zinc-500 dark:text-zinc-400">
            <li>• Widget interne : ✅ Fonctionne normalement</li>
            <li>• Widget embed externe : ❌ Bloqué (HTTP 403)</li>
            <li>• Idéal pour : Développement et tests</li>
          </ul>
        </div>

        <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
          <h3 className="flex items-center gap-2 font-medium text-zinc-900 dark:text-zinc-100">
            <Rocket className="h-5 w-5 text-green-600 dark:text-green-400" />
            Mode Production
          </h3>
          <ul className="mt-2 space-y-1 text-sm text-zinc-500 dark:text-zinc-400">
            <li>• Widget interne : ✅ Fonctionne normalement</li>
            <li>• Widget embed externe : ✅ Autorisé</li>
            <li>• Idéal pour : Déploiement public</li>
          </ul>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}
    </div>
  )
}
